import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";

export default function Footer() {
    const { data: settings } = useSettings();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("Subscribed to our newsletter");
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe");
        } finally {
            setLoading(false);
        }
    };

    const s = settings || {};

    return (
        <footer className="bg-primary text-primary-foreground mt-24">
            <div className="mx-auto max-w-7xl px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="https://res.cloudinary.com/dzvg93xwe/image/upload/v1781978303/white-logo_x5chpy.png" alt="Logo" className="h-9 w-9" />
                        <span className="font-display text-2xl">Tishya Foundation</span>
                    </Link>
                    <p className="mt-5 text-sm leading-relaxed text-primary-foreground/80 max-w-sm">
                        A Lucknow-based charitable trust building a compassionate and sustainable
                        society through environment, education, food and social welfare programs.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        {[
                            { icon: Facebook, url: s.facebook_url, label: "Facebook" },
                            { icon: Instagram, url: s.instagram_url, label: "Instagram" },
                            { icon: Twitter, url: s.twitter_url, label: "Twitter" },
                            { icon: Youtube, url: s.youtube_url, label: "Youtube" },
                        ].map(({ icon: Icon, url, label }) => (
                            <a
                                key={label}
                                href={url || "#"}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`social-${label.toLowerCase()}`}
                                className="h-9 w-9 grid place-items-center rounded-full border border-primary-foreground/30 hover:bg-primary-foreground hover:text-primary transition-colors"
                                aria-label={label}
                            >
                                <Icon className="h-4 w-4" />
                            </a>
                        ))}
                    </div>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-display text-sm font-eyebrow text-secondary mb-4">Explore</h4>
                    <ul className="space-y-2 text-sm text-primary-foreground/80">
                        <li><Link to="/about" className="hover:text-primary-foreground">About</Link></li>
                        <li><Link to="/programs" className="hover:text-primary-foreground">Programs</Link></li>
                        <li><Link to="/projects" className="hover:text-primary-foreground">Projects</Link></li>
                        <li><Link to="/events" className="hover:text-primary-foreground">Events</Link></li>
                        <li><Link to="/gallery" className="hover:text-primary-foreground">Gallery</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-display text-sm font-eyebrow text-secondary mb-4">Engage</h4>
                    <ul className="space-y-2 text-sm text-primary-foreground/80">
                        <li><Link to="/donate" className="hover:text-primary-foreground">Donate</Link></li>
                        <li><Link to="/volunteer" className="hover:text-primary-foreground">Volunteer</Link></li>
                        <li><Link to="/blog" className="hover:text-primary-foreground">Blog</Link></li>
                        <li><Link to="/contact" className="hover:text-primary-foreground">Contact</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <h4 className="font-display text-sm font-eyebrow text-secondary mb-4">Stay in the loop</h4>
                    <p className="text-sm text-primary-foreground/80 mb-4">
                        Get our monthly impact updates and stories from the field.
                    </p>
                    <form onSubmit={subscribe} className="flex gap-2">
                        <Input
                            data-testid="newsletter-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            type="email"
                            required
                            placeholder="your@email.com"
                            className="bg-primary-foreground/10 border-primary-foreground/30 placeholder:text-primary-foreground/50 text-primary-foreground"
                        />
                        <Button
                            data-testid="newsletter-submit"
                            type="submit"
                            disabled={loading}
                            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                        >
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </form>

                    <div className="mt-6 space-y-2 text-sm text-primary-foreground/80">
                        <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{s.contact_address}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0" />
                            <a href={`tel:${s.contact_phone}`} className="hover:text-primary-foreground">{s.contact_phone}</a>
                        </div>
                        <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 shrink-0" />
                            <a href={`mailto:${s.contact_email}`} className="hover:text-primary-foreground">{s.contact_email}</a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-t border-primary-foreground/15">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/70">
                    <div>© {new Date().getFullYear()} Tishya Foundation. Registered Charitable Trust, Lucknow.</div>
                    <div className="font-eyebrow text-[10px]">Empathy · Action · Transparency</div>
                </div>
            </div>
        </footer>
    );
}
